import { useForceDark } from '@/lib/useForceDark';
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { isValidUrl } from '@/lib/validation';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Sparkles, AlertCircle } from 'lucide-react';
import { HOW_HEARD_OPTIONS, WEDDINGS_OPTIONS } from '@/lib/form-data';
import { gaEvent } from '@/lib/ga';
import { AuthShell, AuthLabel, AUTH_PANEL, AUTH_INPUT, AUTH_BUTTON, AUTH_LINK, AUTH_HEADING } from '@/components/AuthShell';
import { TextWithEmailLinks } from '@/components/AccountOnHold';
import { onHoldSentence, ON_HOLD_TOAST } from '@/lib/accountOnHold';

const WELCOME_POINTS = [
  'Place Orders & Track Project Status',
  'Review Edits with Timestamped Notes',
  'Send Footage & Upload Files',
  'Chat Directly with Your Project Manager',
  'Approve Finals & Download Deliverables',
];

/** The sign-in page's notice: dimmed page, the site panel with its ember rule and icon, closed by the backdrop. */
function SignInNotice({ testid, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 animate-fade-in" onClick={onClose}>
      <div className="w-full max-w-md mx-4 animate-fade-in-up" onClick={e => e.stopPropagation()} data-testid={testid}>
        <div className={`${AUTH_PANEL} overflow-hidden`}>
          {/* Ember rule, the website's small editorial mark */}
          <div className="h-[3px] bg-site-ember" />

          <div className="p-8 text-center">
            {/* Icon */}
            <div className="w-14 h-14 border border-site-line flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-7 h-7 text-site-paper" strokeWidth={1.5} />
            </div>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  useForceDark();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ first_name:'', last_name:'', company_name:'', company_website:'', email:'', password:'', confirm_password:'', how_heard:'', how_heard_other:'', weddings_per_year:'' });
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [loginError, setLoginError] = useState(false);
  // An account on hold, signed in with the right password: the server's sentence (lib/accountOnHold.js). A wrong
  // password on it is refused like any other wrong password, so this is never shown to someone without one.
  const [onHold, setOnHold] = useState(null);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const set = (k,v) => setForm(p => ({...p, [k]: v}));

  // Dynamically load reCAPTCHA only on this page, hide the badge (v3 allows this with text attribution)
  useEffect(() => {
    const RECAPTCHA_KEY = '6LcRi3csAAAAANj3uuIWS1G51quj1A3QGBmvM3KI';
    if (document.querySelector('script[src*="recaptcha"]')) return;
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_KEY}`;
    script.async = true;
    document.head.appendChild(script);
    // Hide the badge immediately when it appears. It keeps its own position:fixed. Made absolute, its right:-186px
    // (Google parks it off the edge) widened the page by 186px, so a phone could drag the login page sideways.
    const hideBadge = () => {
      const badge = document.querySelector('.grecaptcha-badge');
      if (badge) { badge.style.visibility = 'hidden'; badge.style.opacity = '0'; }
    };
    script.onload = () => setTimeout(hideBadge, 500);
    const iv = setInterval(hideBadge, 1000);
    return () => {
      clearInterval(iv);
      script.remove();
      hideBadge();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'register' && form.password !== form.confirm_password) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
        navigate('/dashboard');
      } else {
        const heard = form.how_heard === 'Other' ? form.how_heard_other : form.how_heard;
        if (form.company_website && !isValidUrl(form.company_website)) { toast.error('Please enter a valid website URL'); setLoading(false); return; }
        // Get reCAPTCHA token — required for registration
        let recaptcha_token = '';
        try {
          if (window.grecaptcha) {
            recaptcha_token = await window.grecaptcha.execute('6LcRi3csAAAAANj3uuIWS1G51quj1A3QGBmvM3KI', { action: 'register' });
          }
        } catch (rcErr) {
          console.warn('reCAPTCHA execute failed:', rcErr);
        }
        if (!recaptcha_token) {
          toast.error('Security verification failed to load. Please refresh the page and try again.');
          setLoading(false);
          return;
        }
        await register({
          email: form.email, password: form.password,
          name: `${form.first_name} ${form.last_name}`.trim(),
          first_name: form.first_name, last_name: form.last_name,
          company_name: form.company_name, company_website: form.company_website,
          how_heard: heard, weddings_per_year: form.weddings_per_year, role: 'client',
          recaptcha_token,
        });
        // GA4 signup conversion — the event Google Ads imports as "Sign-ups".
        // Fired exactly here (register() resolved = account really created) so
        // it happens once per signup: a retry after a failed attempt never
        // reaches this line, and this page unmounts on the navigate below.
        gaEvent('account_registration');
        toast.success('Account created! Check your email to verify.');
        try { navigate('/style-profile'); } catch { window.location.href = '/style-profile'; }
      }
    } catch (err) {
      if (mode === 'login') {
        const held = onHoldSentence(err);
        if (held) { toast.dismiss(ON_HOLD_TOAST); setOnHold(held); }   // the panel says it; not twice on one screen
        else setLoginError(true);
      } else {
        const detail = err.response?.data?.detail;
        const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ') : 'Registration failed. Please try again.';
        toast.error(msg);
      }
    }
    finally { setLoading(false); }
  };

  return (
    <AuthShell>
      <div className="flex-1 flex items-start justify-center px-4 sm:px-6 pb-16">
        <div className="w-full max-w-4xl flex flex-col md:flex-row gap-6 md:gap-16 animate-stagger-3 items-stretch">

          {/* The form comes first, so a phone shows the whole login card without scrolling (operator, 2026-09-27).
              On a desktop the welcome list moves back to its left (md:order-first below). */}
          <div className={mode === 'register' ? 'w-full max-w-2xl mx-auto' : 'flex-1 flex flex-col'}>
            <div className={`${AUTH_PANEL} p-6 sm:p-8 ${mode === 'register' ? '' : 'flex-1 flex flex-col'}`}>
              <div className="mb-6 sm:mb-8">
                <h2 className={`${AUTH_HEADING} text-2xl mb-2`}>
                  {mode === 'login' ? 'Client Portal' : 'Create Your Account'}
                </h2>
                <p className="font-sans text-[13px] text-site-muted leading-relaxed">
                  {mode === 'login' ? 'Please enter your login details to access our powerful collaboration platform.' : 'Sign up as a new client to get started'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className={mode === 'login' ? 'flex-1 flex flex-col justify-between gap-6 sm:gap-8' : 'space-y-6'} data-testid="auth-form">
                {mode === 'register' ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <AuthLabel required>First Name</AuthLabel>
                        <Input data-testid="register-first-name" value={form.first_name} onChange={e => set('first_name', e.target.value)} required placeholder="John"
                          className={AUTH_INPUT} />
                      </div>
                      <div className="space-y-2">
                        <AuthLabel required>Last Name</AuthLabel>
                        <Input data-testid="register-last-name" value={form.last_name} onChange={e => set('last_name', e.target.value)} required placeholder="Doe"
                          className={AUTH_INPUT} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <AuthLabel required>Company Name</AuthLabel>
                        <Input data-testid="register-company" value={form.company_name} onChange={e => set('company_name', e.target.value)} required placeholder="Studio name"
                          className={AUTH_INPUT} />
                      </div>
                      <div className="space-y-2">
                        <AuthLabel>Company Website</AuthLabel>
                        <Input data-testid="register-website" value={form.company_website} onChange={e => set('company_website', e.target.value)} placeholder="https://yourstudio.com"
                          className={AUTH_INPUT} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <AuthLabel required>Email</AuthLabel>
                        <Input data-testid="email-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} required placeholder="john@example.com"
                          className={AUTH_INPUT} />
                      </div>
                      <div className="space-y-2">
                        <AuthLabel required>How Did You Hear About Us?</AuthLabel>
                        <Select value={form.how_heard} onValueChange={v => set('how_heard', v)}>
                          <SelectTrigger className="bg-transparent border-0 border-b border-site-rule rounded-none text-site-paper h-11 px-0 text-sm focus:ring-0 focus:ring-offset-0 focus:border-site-teal">
                            <SelectValue placeholder="Select one..." />
                          </SelectTrigger>
                          <SelectContent className="bg-site-panel border-site-line rounded-none">
                            {HOW_HEARD_OPTIONS.map(opt => <SelectItem key={opt} value={opt} className="text-site-paper text-xs rounded-none">{opt}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {form.how_heard === 'Other' && (
                          <Input value={form.how_heard_other} onChange={e => set('how_heard_other', e.target.value)} placeholder="Please specify"
                            className={`${AUTH_INPUT} h-9 mt-1`} />
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <AuthLabel required>Password</AuthLabel>
                        <Input data-testid="password-input" type="password" value={form.password} onChange={e => set('password', e.target.value)} required placeholder="Min 8 characters"
                          className={AUTH_INPUT} />
                      </div>
                      <div className="space-y-2">
                        <AuthLabel required>Confirm Password</AuthLabel>
                        <Input data-testid="confirm-password-input" type="password" value={form.confirm_password} onChange={e => set('confirm_password', e.target.value)} required placeholder="Re-enter password"
                          className={AUTH_INPUT} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <AuthLabel required>How Many Weddings Per Year Do You Do?</AuthLabel>
                      <div className="flex flex-wrap gap-2 pt-1">
                        {WEDDINGS_OPTIONS.map(opt => (
                          <button key={opt} type="button" onClick={() => set('weddings_per_year', opt)}
                            className={`px-4 py-2.5 rounded-none border font-azeret text-[11px] tracking-wider transition-colors
                              ${form.weddings_per_year === opt ? 'border-site-teal bg-site-teal/10 text-site-teal' : 'border-site-line text-site-muted hover:border-site-rule'}`}>
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                    <Button type="submit" data-testid="auth-submit-btn" disabled={loading} className={`${AUTH_BUTTON} mt-2`}>
                      {loading ? 'Creating...' : 'Create Account'}
                    </Button>
                    <p className="text-[11px] text-site-muted text-center mt-3 leading-relaxed">
                      By creating an account, you agree to our <a href="/terms" target="_blank" className="text-site-teal underline decoration-site-teal underline-offset-2">Terms of Service</a> and <a href="/privacy" target="_blank" className="text-site-teal underline decoration-site-teal underline-offset-2">Privacy Policy</a>.
                    </p>
                    <div className="mt-4">
                      <button onClick={() => setMode('login')} data-testid="toggle-auth-mode"
                        className="font-barlow text-sm font-semibold uppercase tracking-[0.1em] text-site-muted">
                        Already have an account? <span className={AUTH_LINK}>Log In</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <AuthLabel required>Email</AuthLabel>
                        <Input data-testid="email-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} required placeholder="john@example.com"
                          className={AUTH_INPUT} />
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <AuthLabel required>Password</AuthLabel>
                          <button type="button" tabIndex={-1} onClick={() => setShowPw(!showPw)} className="font-azeret text-[9px] uppercase tracking-[0.2em] text-site-muted hover:text-site-teal flex items-center gap-1">
                            <Sparkles className="w-3 h-3" /> {showPw ? 'Hide' : 'Show'}
                          </button>
                        </div>
                        <Input data-testid="password-input" type={showPw ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)} required placeholder="Enter password"
                          className={AUTH_INPUT} />
                        <div className="text-right mt-2">
                          <a href="/forgot-password" data-testid="forgot-password-link" className={`${AUTH_LINK} text-[12px]`}>
                            Forgot Password?
                          </a>
                        </div>
                      </div>
                    </div>
                    <div>
                      <Button type="submit" data-testid="auth-submit-btn" disabled={loading} className={AUTH_BUTTON}>
                        {loading ? 'Please wait...' : 'Login'}
                      </Button>
                    </div>
                  </>
                )}
              </form>

              {/* Quick Access removed for production. The previous portal's link went on 2026-09-26: nobody
                  uses the old portal now, even for old projects (operator). */}
            </div>
          </div>

          {/* Welcome + bullet points — only on login mode. On a phone they follow the card, "Not a member? Sign up"
              first so it sits right under the card; on a desktop this is the left column and the line is its foot. */}
          {mode === 'login' && (
            <div className="flex-1 flex flex-col md:order-first">
              <div className="mb-10 md:mb-0 md:order-last">
                <button onClick={() => setMode('register')} data-testid="toggle-auth-mode"
                  className="font-barlow text-sm font-semibold uppercase tracking-[0.1em] text-site-muted">
                  Not a member? <span className={AUTH_LINK}>Sign Up</span>
                </button>
              </div>
              <h2 className="font-anton uppercase text-site-paper leading-[0.85] tracking-[-0.02em]" style={{ fontSize: 'clamp(44px, 6vw, 72px)' }}>
                Welcome
              </h2>
              <div className="flex-1 flex flex-col justify-evenly gap-4 py-7">
                {WELCOME_POINTS.map((point, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <span aria-hidden="true" className="w-[7px] h-[7px] bg-site-teal rotate-45 shrink-0" />
                    <span className="font-barlow text-[15px] font-semibold uppercase tracking-[0.1em] text-site-paper/85">{point}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Login Error Modal */}
      {loginError && (
        <SignInNotice testid="login-error-modal" onClose={() => setLoginError(false)}>
          {/* Title — neutral, accurate */}
          <h2 className="font-anton uppercase text-site-paper text-[28px] leading-[0.95] tracking-[-0.01em] mb-3" data-testid="login-error-title">Incorrect email or password</h2>

          {/* Message — assume a typo, not a missing account */}
          <p className="font-sans text-sm text-site-paper/70 leading-relaxed mb-6" data-testid="login-error-message">
            The email or password you entered doesn't match. Double-check your details and try again, or reset your password.
          </p>

          {/* Primary actions */}
          <div className="space-y-3">
            <Button onClick={() => setLoginError(false)} data-testid="error-try-again-btn" className={AUTH_BUTTON}>
              Try Again
            </Button>

            <a href={`/forgot-password?email=${encodeURIComponent(form.email || '')}`} data-testid="error-reset-password-btn"
              className="w-full h-12 border border-site-teal hover:bg-site-teal/10 flex items-center justify-center transition-colors font-barlow font-bold text-[15px] uppercase tracking-[0.12em] text-site-teal">
              Reset Password
            </a>
          </div>

          {/* Secondary, demoted hints */}
          <div className="mt-6 pt-5 border-t border-site-line">
            <p className="font-sans text-[12px] text-site-muted leading-relaxed">
              First time here?{' '}
              <button onClick={() => { setLoginError(false); setMode('register'); }} data-testid="error-signup-link" className={AUTH_LINK}>
                Create an account
              </button>
            </p>
          </div>
        </SignInNotice>
      )}

      {/* An account on hold: the server's one sentence, nothing else to read, nothing to reset */}
      {onHold && (
        <SignInNotice testid="login-on-hold-modal" onClose={() => setOnHold(null)}>
          <p className="font-sans text-base text-site-paper leading-relaxed mb-6" data-testid="login-on-hold-message">
            <TextWithEmailLinks text={onHold} linkClassName="text-site-teal underline decoration-site-teal underline-offset-4 hover:text-site-paper transition-colors" />
          </p>
          <Button onClick={() => setOnHold(null)} data-testid="on-hold-close-btn" className={AUTH_BUTTON}>
            Close
          </Button>
        </SignInNotice>
      )}
    </AuthShell>
  );
}
